import { isAlarmChannelMessageType, WsEventType, WsMessageType, type WsBaseMessage } from '@/shared/realtime/ws-types';

import type { BoardDeviceRealtimeEvent } from './board-device-realtime-event';

export type BoardAlarmSeverity = 'danger' | 'info' | 'warning';
export type BoardAlarmTransition = 'activate' | 'clear' | 'ignore';
export type BoardAlarmDeliverySource = 'device' | 'persisted';

export interface BoardAlarmRealtimeEvent extends BoardDeviceRealtimeEvent {
  alarmUid: string;
  clearedAlarmUid: string;
  eventAt: string;
  eventAtMs: number;
  eventUid: string;
  deliverySource: BoardAlarmDeliverySource;
  markerIdentities: readonly string[];
  occurrenceId: string;
  severity: BoardAlarmSeverity | null;
  shouldAutoFocus: boolean;
  sourceSequence: number | null;
  targetDeviceUid: string;
  transition: BoardAlarmTransition;
}

export type BoardAlarmEventOrder = Pick<BoardAlarmRealtimeEvent, 'eventAtMs' | 'sourceSequence'>;

export type BoardAlarmRealtimeCursor = BoardAlarmEventOrder & Pick<BoardAlarmRealtimeEvent, 'eventId' | 'transition'>;

export type BoardAlarmRealtimeAcceptanceState = {
  latestEventByStreamKey: Map<string, BoardAlarmRealtimeEvent>;
  latestByStreamKey: Map<string, BoardAlarmRealtimeCursor>;
  seenEventIdOrder: string[];
  seenEventIds: Set<string>;
};

export type BoardAlarmRealtimeAcceptanceResult = {
  accepted: boolean;
  state: BoardAlarmRealtimeAcceptanceState;
};

export type BoardAlarmOccurrenceProjectionState = {
  activeByOccurrenceId: Map<string, BoardAlarmRealtimeEvent>;
  occurrenceIdByAlarmUid: Map<string, string>;
  occurrenceIdByCorrelationId: Map<string, string>;
  latestOccurrenceIdByStreamKey: Map<string, string>;
};

export type BoardAlarmOccurrenceProjectionResult = {
  projection: readonly BoardAlarmRealtimeEvent[];
  triggerEvent: BoardAlarmRealtimeEvent | null;
  updatedEvent: BoardAlarmRealtimeEvent | null;
};

const BOARD_ALARM_SEEN_EVENT_LIMIT = 512;
const BOARD_ALARM_STREAM_CURSOR_LIMIT = 512;
const BOARD_ALARM_OCCURRENCE_LIMIT = 512;
const BOARD_ALARM_FALLBACK_MATCH_WINDOW_MS = 120_000;

/** 같은 알람 stream에서 source sequence를 우선하고 없으면 발생 시각으로 역순 이벤트를 거릅니다. */
export function isBoardAlarmRealtimeEventNewer(latest: BoardAlarmEventOrder, incoming: BoardAlarmEventOrder): boolean {
  if (latest.sourceSequence !== null && incoming.sourceSequence !== null) return incoming.sourceSequence > latest.sourceSequence;
  return incoming.eventAtMs > latest.eventAtMs;
}

export function createBoardAlarmRealtimeAcceptanceState(): BoardAlarmRealtimeAcceptanceState {
  return {
    latestEventByStreamKey: new Map(),
    latestByStreamKey: new Map(),
    seenEventIdOrder: [],
    seenEventIds: new Set(),
  };
}

export function createBoardAlarmOccurrenceProjectionState(): BoardAlarmOccurrenceProjectionState {
  return {
    activeByOccurrenceId: new Map(),
    occurrenceIdByAlarmUid: new Map(),
    occurrenceIdByCorrelationId: new Map(),
    latestOccurrenceIdByStreamKey: new Map(),
  };
}

function resolveBoardAlarmOccurrenceStreamKey(event: BoardAlarmRealtimeEvent): string {
  const targetKey = event.targetDeviceUid || event.displayDeviceUid || event.deviceGroupUid || 'unknown';
  return `${event.fieldUid}:${String(event.message.event)}:${targetKey}`;
}

function deleteBoardAlarmOccurrence(state: BoardAlarmOccurrenceProjectionState, occurrenceId: string): void {
  const event = state.activeByOccurrenceId.get(occurrenceId);
  if (!event) return;

  state.activeByOccurrenceId.delete(occurrenceId);
  if (event.alarmUid && state.occurrenceIdByAlarmUid.get(event.alarmUid) === occurrenceId) state.occurrenceIdByAlarmUid.delete(event.alarmUid);
  if (event.correlationId && state.occurrenceIdByCorrelationId.get(event.correlationId) === occurrenceId) {
    state.occurrenceIdByCorrelationId.delete(event.correlationId);
  }
  const streamKey = resolveBoardAlarmOccurrenceStreamKey(event);
  if (state.latestOccurrenceIdByStreamKey.get(streamKey) === occurrenceId) state.latestOccurrenceIdByStreamKey.delete(streamKey);
}

function rememberBoardAlarmOccurrence(state: BoardAlarmOccurrenceProjectionState, event: BoardAlarmRealtimeEvent): void {
  state.activeByOccurrenceId.delete(event.occurrenceId);
  state.activeByOccurrenceId.set(event.occurrenceId, event);
  if (event.alarmUid) state.occurrenceIdByAlarmUid.set(event.alarmUid, event.occurrenceId);
  if (event.correlationId) state.occurrenceIdByCorrelationId.set(event.correlationId, event.occurrenceId);
  state.latestOccurrenceIdByStreamKey.set(resolveBoardAlarmOccurrenceStreamKey(event), event.occurrenceId);

  while (state.activeByOccurrenceId.size > BOARD_ALARM_OCCURRENCE_LIMIT) {
    const oldestOccurrenceId = state.activeByOccurrenceId.keys().next().value;
    if (!oldestOccurrenceId) break;
    deleteBoardAlarmOccurrence(state, oldestOccurrenceId);
  }
}

function resolveExistingBoardAlarmOccurrenceId(state: BoardAlarmOccurrenceProjectionState, event: BoardAlarmRealtimeEvent): string {
  if (event.correlationId) {
    const occurrenceId = state.occurrenceIdByCorrelationId.get(event.correlationId);
    if (occurrenceId) return occurrenceId;
  }
  if (event.alarmUid) {
    const occurrenceId = state.occurrenceIdByAlarmUid.get(event.alarmUid);
    if (occurrenceId) return occurrenceId;
  }

  // 상관 식별자가 없는 구형 payload만 즉시 DEVICE → 지연 ALARM 순서에서 보강한다.
  // DEVICE 재알람은 같은 장비·이벤트라도 매번 새 occurrence여야 한다.
  const streamOccurrenceId = state.latestOccurrenceIdByStreamKey.get(resolveBoardAlarmOccurrenceStreamKey(event));
  if (!streamOccurrenceId) return '';
  const current = state.activeByOccurrenceId.get(streamOccurrenceId);
  if (
    !current ||
    current.deliverySource !== 'device' ||
    event.deliverySource !== 'persisted' ||
    (current.correlationId && event.correlationId) ||
    event.receivedAtMs - current.receivedAtMs > BOARD_ALARM_FALLBACK_MATCH_WINDOW_MS
  ) {
    return '';
  }
  return streamOccurrenceId;
}

function mergeBoardAlarmOccurrence(current: BoardAlarmRealtimeEvent, incoming: BoardAlarmRealtimeEvent): BoardAlarmRealtimeEvent {
  const preferIncomingMessage = incoming.deliverySource === 'persisted';
  return {
    ...incoming,
    alarmUid: incoming.alarmUid || current.alarmUid,
    correlationId: incoming.correlationId || current.correlationId,
    deviceGroupUid: incoming.deviceGroupUid || current.deviceGroupUid,
    displayDeviceUid: incoming.displayDeviceUid || current.displayDeviceUid,
    eventAt: current.eventAt,
    eventAtMs: current.eventAtMs,
    eventUid: current.eventUid || incoming.eventUid,
    markerIdentities: uniqueStrings([...current.markerIdentities, ...incoming.markerIdentities]),
    message: preferIncomingMessage ? incoming.message : current.message,
    occurrenceId: current.occurrenceId,
    receivedAt: current.receivedAt,
    receivedAtMs: current.receivedAtMs,
    sentAtMs: current.sentAtMs,
    sourceDeviceUid: incoming.sourceDeviceUid || current.sourceDeviceUid,
    sourceMessageKey: incoming.sourceMessageKey || current.sourceMessageKey,
    sourceTraceId: incoming.sourceTraceId || current.sourceTraceId,
    targetDeviceUid: incoming.targetDeviceUid || current.targetDeviceUid,
  };
}

/** 즉시 DEVICE와 저장 완료 ALARM delivery를 하나의 활성 occurrence로 병합합니다. */
export function reduceBoardAlarmOccurrenceProjection(state: BoardAlarmOccurrenceProjectionState, event: BoardAlarmRealtimeEvent): BoardAlarmOccurrenceProjectionResult {
  if (event.transition === 'ignore') return { projection: [...state.activeByOccurrenceId.values()], triggerEvent: null, updatedEvent: null };

  if (event.transition === 'clear') {
    const matchedOccurrenceIds = new Set<string>();
    const alarmUid = event.clearedAlarmUid || event.alarmUid;
    if (alarmUid) {
      const occurrenceId = state.occurrenceIdByAlarmUid.get(alarmUid);
      if (occurrenceId) matchedOccurrenceIds.add(occurrenceId);
    }
    if (event.correlationId) {
      const occurrenceId = state.occurrenceIdByCorrelationId.get(event.correlationId);
      if (occurrenceId) matchedOccurrenceIds.add(occurrenceId);
    }
    if (matchedOccurrenceIds.size === 0) {
      state.activeByOccurrenceId.forEach((activeEvent, occurrenceId) => {
        if ((event.targetDeviceUid && activeEvent.targetDeviceUid === event.targetDeviceUid) || (event.deviceGroupUid && activeEvent.deviceGroupUid === event.deviceGroupUid)) {
          matchedOccurrenceIds.add(occurrenceId);
        }
      });
    }
    const occurrenceId = matchedOccurrenceIds.values().next().value || event.correlationId || event.eventId;
    matchedOccurrenceIds.forEach(id => deleteBoardAlarmOccurrence(state, id));
    const clearEvent = { ...event, occurrenceId };
    return { projection: [...state.activeByOccurrenceId.values()], triggerEvent: clearEvent, updatedEvent: clearEvent };
  }

  const existingOccurrenceId = resolveExistingBoardAlarmOccurrenceId(state, event);
  const current = existingOccurrenceId ? state.activeByOccurrenceId.get(existingOccurrenceId) : undefined;
  if (current) {
    const updatedEvent = mergeBoardAlarmOccurrence(current, event);
    rememberBoardAlarmOccurrence(state, updatedEvent);
    return { projection: [...state.activeByOccurrenceId.values()], triggerEvent: null, updatedEvent };
  }

  const correlationPart = event.correlationId || 'delivery';
  const occurrenceId = `${event.fieldUid}:${correlationPart}:${event.eventId}`;
  const occurrenceEvent = { ...event, occurrenceId };
  rememberBoardAlarmOccurrence(state, occurrenceEvent);
  return { projection: [...state.activeByOccurrenceId.values()], triggerEvent: occurrenceEvent, updatedEvent: occurrenceEvent };
}

function resolveBoardAlarmRealtimeStreamKey(event: BoardAlarmRealtimeEvent): string {
  const targetKey = event.alarmUid || event.clearedAlarmUid || event.targetDeviceUid || event.deviceGroupUid || event.eventId;
  return `${event.fieldUid}:${targetKey}`;
}

function canAdvanceBoardAlarmStream(latest: BoardAlarmRealtimeCursor, incoming: BoardAlarmRealtimeEvent): boolean {
  if (latest.transition === 'clear' && incoming.transition === 'activate') {
    // 해제된 lifecycle 뒤의 새 발생은 같은 alarm UID와 timestamp를 재사용해도 별도 occurrence면 다시 엽니다.
    return incoming.eventAtMs >= latest.eventAtMs;
  }
  if (incoming.transition === 'clear' && latest.eventAtMs === incoming.eventAtMs && (latest.sourceSequence === null || incoming.sourceSequence === null)) {
    return true;
  }
  return isBoardAlarmRealtimeEventNewer(latest, incoming);
}

function setBoundedBoardAlarmStreamCursor(state: BoardAlarmRealtimeAcceptanceState, streamKey: string, cursor: BoardAlarmRealtimeCursor): void {
  state.latestEventByStreamKey.delete(streamKey);
  state.latestByStreamKey.delete(streamKey);
  state.latestByStreamKey.set(streamKey, cursor);

  while (state.latestByStreamKey.size > BOARD_ALARM_STREAM_CURSOR_LIMIT) {
    const oldestStreamKey = state.latestByStreamKey.keys().next().value;
    if (oldestStreamKey === undefined) break;
    state.latestByStreamKey.delete(oldestStreamKey);
    state.latestEventByStreamKey.delete(oldestStreamKey);
  }
}

function rememberBoardAlarmEventId(state: BoardAlarmRealtimeAcceptanceState, eventId: string): void {
  state.seenEventIdOrder.push(eventId);
  state.seenEventIds.add(eventId);

  while (state.seenEventIdOrder.length > BOARD_ALARM_SEEN_EVENT_LIMIT) {
    const removedEventId = state.seenEventIdOrder.shift();
    if (removedEventId) state.seenEventIds.delete(removedEventId);
  }
}

/** Provider ref가 소유한 bounded 상태를 갱신해 중복·역순 수신을 제거하고 clear 이후 새 occurrence는 허용합니다. */
export function reduceBoardAlarmRealtimeAcceptance(state: BoardAlarmRealtimeAcceptanceState, event: BoardAlarmRealtimeEvent): BoardAlarmRealtimeAcceptanceResult {
  if (state.seenEventIds.has(event.eventId)) return { accepted: false, state };

  const streamKey = resolveBoardAlarmRealtimeStreamKey(event);
  const latest = state.latestByStreamKey.get(streamKey);
  if (latest && !canAdvanceBoardAlarmStream(latest, event)) return { accepted: false, state };

  rememberBoardAlarmEventId(state, event.eventId);
  setBoundedBoardAlarmStreamCursor(state, streamKey, {
    eventAtMs: event.eventAtMs,
    eventId: event.eventId,
    sourceSequence: event.sourceSequence,
    transition: event.transition,
  });
  state.latestEventByStreamKey.set(streamKey, event);
  return { accepted: true, state };
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : null;
}

function collectPayloadSources(message: WsBaseMessage): Record<string, unknown>[] {
  const body = asRecord(message.body) ?? {};
  const meta = asRecord(body.meta);
  const bodyDetail = asRecord(body.detail);
  const metaDetail = asRecord(meta?.detail);
  return [body, meta, bodyDetail, metaDetail, asRecord(bodyDetail?.body), asRecord(metaDetail?.body), asRecord(body.data)].filter(
    (source): source is Record<string, unknown> => source !== null
  );
}

function readString(sources: readonly Record<string, unknown>[], keys: readonly string[]): string {
  for (const key of keys) {
    for (const source of sources) {
      const value = source[key];
      if (typeof value === 'string' && value.trim()) return value.trim();
    }
  }
  return '';
}

function readNumber(sources: readonly Record<string, unknown>[], keys: readonly string[]): number | null {
  for (const key of keys) {
    for (const source of sources) {
      const value = source[key];
      const parsed = typeof value === 'number' ? value : typeof value === 'string' && value.trim() ? Number(value) : NaN;
      if (Number.isFinite(parsed)) return parsed;
    }
  }
  return null;
}

function readProductValue(source: Record<string, unknown>, ...keys: string[]): string | number | undefined {
  for (const key of keys) {
    const value = source[key];
    if (typeof value === 'string' && value.trim()) return value.trim();
    if (typeof value === 'number' && Number.isFinite(value)) return Math.trunc(value);
  }
  return undefined;
}

function readSourceString(source: Record<string, unknown>, ...keys: string[]): string {
  for (const key of keys) {
    const value = source[key];
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return '';
}

function resolveProductIdentity(productValue: string | number | undefined, rId: string, sId: string): string {
  if (productValue === undefined || !rId || !sId || ![rId, sId].some(value => /[1-9A-Za-z]/.test(value))) return '';
  return `product:${String(productValue)}:r:${rId}:s:${sId}`;
}

function uniqueStrings(values: readonly unknown[]): string[] {
  return Array.from(new Set(values.filter((value): value is string => typeof value === 'string' && value.trim().length > 0).map(value => value.trim())));
}

function collectScopedIdentities(sources: readonly Record<string, unknown>[], scope: 'display' | 'primary' | 'source'): string[] {
  return uniqueStrings(
    sources.flatMap(source => {
      if (scope === 'display') {
        const rId = readSourceString(source, 'display_r_id', 'displayRId');
        const sId = readSourceString(source, 'display_s_id', 'displaySId');
        return [
          source.display_device_uid,
          source.displayDeviceUid,
          source.display_device_code,
          source.displayDeviceCode,
          resolveProductIdentity(readProductValue(source, 'display_product_uid', 'displayProductUid'), rId, sId),
          resolveProductIdentity(readProductValue(source, 'display_product_sensor_type', 'displayProductSensorType'), rId, sId),
        ];
      }
      if (scope === 'primary') {
        const rId = readSourceString(source, 'r_id', 'rId');
        const sId = readSourceString(source, 's_id', 'sId');
        return [
          source.device_uid,
          source.deviceUid,
          source.device_code,
          source.deviceCode,
          resolveProductIdentity(readProductValue(source, 'product_uid', 'productUid'), rId, sId),
          resolveProductIdentity(readProductValue(source, 'product_sensor_type', 'productSensorType', 'sensor_type', 'sensorType'), rId, sId),
        ];
      }

      const sourceRId = readSourceString(source, 'source_r_id', 'sourceRId');
      const sourceSId = readSourceString(source, 'source_s_id', 'sourceSId');
      const originalRId = readSourceString(source, 'original_r_id', 'originalRId');
      const originalSId = readSourceString(source, 'original_s_id', 'originalSId');
      return [
        source.source_device_uid,
        source.sourceDeviceUid,
        source.source_device_code,
        source.sourceDeviceCode,
        resolveProductIdentity(readProductValue(source, 'source_product_uid', 'sourceProductUid'), sourceRId, sourceSId),
        resolveProductIdentity(readProductValue(source, 'source_product_sensor_type', 'sourceProductSensorType'), sourceRId, sourceSId),
        source.original_device_uid,
        source.originalDeviceUid,
        source.original_device_code,
        source.originalDeviceCode,
        resolveProductIdentity(readProductValue(source, 'original_product_uid', 'originalProductUid'), originalRId, originalSId),
        resolveProductIdentity(readProductValue(source, 'original_product_sensor_type', 'originalProductSensorType'), originalRId, originalSId),
      ];
    })
  );
}

function resolveMarkerIdentities(sources: readonly Record<string, unknown>[]): string[] {
  for (const scope of ['display', 'primary', 'source'] as const) {
    const identities = collectScopedIdentities(sources, scope);
    if (identities.length > 0) return identities;
  }
  return uniqueStrings(sources.flatMap(source => [source.main_device_uid, source.mainDeviceUid]));
}

function resolveTransition(
  message: WsBaseMessage,
  sources: readonly Record<string, unknown>[],
  clearedAlarmUid: string
): {
  severity: BoardAlarmSeverity | null;
  transition: BoardAlarmTransition;
} {
  const event = String(message.event ?? '')
    .trim()
    .toUpperCase();
  const sourceEvent = readString(sources, ['source_event', 'sourceEvent', 'state_reason', 'stateReason']).toUpperCase();
  if (event === WsEventType.DEVICE_GEOFENCE_RETURNED || clearedAlarmUid || sourceEvent === 'WARNING_CLEARED' || sourceEvent === 'COLLISION_RESOLVED') {
    return { severity: null, transition: 'clear' };
  }
  if (event === WsEventType.SACON_PRODUCT_ALERT || event === WsEventType.DEVICE_GEOFENCE_ESCAPE || event === WsEventType.ALERT || message.type === WsMessageType.ALERT) {
    return { severity: 'danger', transition: 'activate' };
  }
  if (event === WsEventType.SACON_PRODUCT_WARNING || event === WsEventType.WARNING || message.type === WsMessageType.WARN) {
    return { severity: 'warning', transition: 'activate' };
  }
  if (event === WsEventType.SACON_PRODUCT_INFO) return { severity: 'info', transition: 'activate' };
  return { severity: null, transition: 'ignore' };
}

/** ALARM 채널의 중첩 payload를 Map/Tunnel 공용 계약으로 정규화합니다. */
export function normalizeBoardAlarmRealtimeEvent(message: WsBaseMessage, sequence: number): BoardAlarmRealtimeEvent | null {
  const sources = collectPayloadSources(message);
  const fieldUid = (typeof message.field_uid === 'string' ? message.field_uid.trim() : '') || readString(sources, ['field_uid', 'fieldUid']);
  if (!fieldUid) return null;

  const sourceDeviceUid = readString(sources, ['source_device_uid', 'sourceDeviceUid', 'original_device_uid', 'originalDeviceUid']);
  const displayDeviceUid = readString(sources, ['display_device_uid', 'displayDeviceUid', 'device_uid', 'deviceUid']);
  const deviceGroupUid = readString(sources, ['device_group_uid', 'deviceGroupUid']);
  const alarmUid = readString(sources, ['alarm_uid', 'alarmUid']);
  const clearedAlarmUid = readString(sources, ['cleared_alarm_uid', 'clearedAlarmUid']);
  const sourceTraceId = readString(sources, ['source_trace_id', 'sourceTraceId', 'trace_id', 'traceId']);
  const sourceMessageKey = readString(sources, ['source_message_key', 'sourceMessageKey', 'message_key', 'messageKey']);
  const correlationId = readString(sources, ['event_uid', 'eventUid', 'correlation_id', 'correlationId']) || sourceTraceId;
  const explicitEventUid = correlationId || readString(sources, ['uid']);
  const receivedAtMs = Date.now();
  const receivedAt = new Date(receivedAtMs).toISOString();
  const eventAtCandidate = readString(sources, ['event_at', 'eventAt', 'occurred_at', 'occurredAt', 'created_at', 'createdAt']) || message.sent_at;
  const parsedEventAtMs = Date.parse(eventAtCandidate);
  const eventAtMs = Number.isFinite(parsedEventAtMs) ? parsedEventAtMs : receivedAtMs;
  const eventAt = new Date(eventAtMs).toISOString();
  const parsedSentAtMs = Date.parse(message.sent_at);
  const sourceSequence = readNumber(sources, ['sequence', 'event_sequence', 'eventSequence', 'seq']);
  const { severity, transition } = resolveTransition(message, sources, clearedAlarmUid);
  const targetDeviceUid = sourceDeviceUid || displayDeviceUid;
  const eventUid = explicitEventUid || alarmUid || clearedAlarmUid;
  const sourceEventId = explicitEventUid || alarmUid || clearedAlarmUid || [message.type, message.event, message.key, targetDeviceUid, deviceGroupUid].join(':');
  const occurrenceOrder = sourceSequence === null ? `time:${eventAt}:${message.sent_at}` : `sequence:${sourceSequence}`;
  const eventId = `${transition}:${sourceEventId}:${occurrenceOrder}`;

  return {
    alarmUid,
    clearedAlarmUid,
    correlationId,
    deliverySource: isAlarmChannelMessageType(message.type) ? 'persisted' : 'device',
    deviceGroupUid,
    displayDeviceState: '',
    displayDeviceUid,
    eventAt,
    eventAtMs,
    eventId,
    eventUid,
    fieldUid,
    markerIdentities: resolveMarkerIdentities(sources),
    message,
    occurrenceId: '',
    parentDeviceState: '',
    receivedAt,
    receivedAtMs,
    sentAtMs: Number.isFinite(parsedSentAtMs) ? parsedSentAtMs : eventAtMs,
    sequence,
    severity,
    shouldAutoFocus: transition === 'activate' && (severity === 'danger' || severity === 'warning'),
    sourceDeviceState: '',
    sourceDeviceUid,
    sourceMessageKey,
    sourceSequence,
    sourceTraceId,
    targetDeviceUid,
    transition,
  };
}
